import React, { useContext, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { clearStorage, getStorage } from "../helper/storage";
import Header from "../Component/Layout/Header";
import Sidebar from "../Component/Layout/Sidebar";
import classNames from "classnames";
import { getAllAppDetailsApi } from "../services/voterService";
import UserContext from "../helper/UserContext";
import { hasAccess, ROLE } from "../helper";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";

// `feature` is the FEATURES key (helper/roles.config.js) this route requires.
// Enforcing it here (not just hiding the sidebar link) stops a role from
// reaching a page it can't use by typing/bookmarking the URL directly.
export const ProtectRoutes = ({ children, feature }) => {
  const [sidebarIsOpen, setSidebarOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const { user, setUser } = useContext(UserContext);
  const loggedIn = React.useMemo(() => getStorage("logged-in"), []);

  const toggleSidebar = () => setSidebarOpen(!sidebarIsOpen);

  useEffect(() => {
    const fetchDetails = async () => {
      if (loggedIn?.access_token) {
        try {
          const res = await getAllAppDetailsApi({
            app_name: loggedIn?.app_name,
            party_name: loggedIn?.party_name,
          });
          if (res?.status === 200) {
            if (JSON.stringify(user.appDetails) !== JSON.stringify(res?.results[0])) {
              setUser({ ...user, appDetails: res?.results[0] });
            }
            // Drives the sidebar's language switcher (Sidebar.js) and the
            // voter form's Hindi/English toggle (AddVoter.js) - both read
            // "app_available_languages" from localStorage rather than
            // hardcoding which languages exist, so they stay in sync with
            // whatever this app is actually configured for. Always write
            // (even when null/empty) so a null response actively clears any
            // stale list instead of leaving a previous app's languages - or
            // the hardcoded fallback - sitting in localStorage forever.
            const languageSupported = res?.results[0]?.language_supported;
            const parsedLanguages = !languageSupported
              ? []
              : Array.isArray(languageSupported)
              ? languageSupported
              : String(languageSupported)
                  .replace(/[{}]/g, "")
                  .split(",");
            const normalizedLanguages = parsedLanguages
              .map((lang) => String(lang).trim().toLowerCase())
              .filter(Boolean);
            const nextValue = JSON.stringify(normalizedLanguages);
            if (localStorage.getItem("app_available_languages") !== nextValue) {
              localStorage.setItem("app_available_languages", nextValue);
              window.dispatchEvent(new CustomEvent("appLanguagesUpdated"));
            }
          }
        } catch (error) {
          console.error("Error fetching app details:", error);
        }
      }
      setIsLoading(false);
    };
    fetchDetails();
  }, [loggedIn]);

  if (isLoading) return <div>Loading...</div>;
  if (!loggedIn?.access_token) {
  return <Navigate to="/login" />;
  }
  if (feature && !hasAccess(loggedIn?.user_type, feature)) {
    return <Navigate to="/dashboard" replace />;
  }
  return (
    <div className="al_dashboard">
      <div className="">
        <Sidebar toggle={toggleSidebar} isOpen={sidebarIsOpen} />
        <div className={classNames("content", { "is-open": sidebarIsOpen })}>
          <Header toggle={toggleSidebar} />
          {loggedIn?.user_type !== ROLE.SUPER_ADMIN ? (
            user?.appDetails?.is_enable ? (
              <div className="_mainContainer">{children}</div>
            ) : (
              user?.appDetails && (
                <div className="_mainContainer">
                  <Modal
                    size="md"
                    isOpen={!user?.appDetails?.is_enable}
                    backdrop
                    keyboard
                    centered={true}
                  >
                    <ModalHeader>App Important Message</ModalHeader>
                    <ModalBody>
                      {user?.appDetails?.error_message || "No App Details"}
                    </ModalBody>
                    <ModalFooter>
                      <Button color="primary" onClick={clearStorage}>
                        Logout
                      </Button>
                    </ModalFooter>
                  </Modal>
                </div>
              )
            )
          ) : (
            <div className="_mainContainer">{children}</div>
          )}
        </div>
      </div>
    </div>
  );
};
